import { useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import api from "../lib/api";
import { track } from "../lib/usageTracker";
import { ActionButton, SecondaryAction } from "./kit";

/**
 * Issue #975 (D64/D186.7, D57) — the Settings-A SIBLING of
 * `CoachAccessInviteActions.jsx`, not an edit to it (the issue's own
 * "stop-and-ask before editing its default" — this avoids that question
 * entirely by not touching the shipped component's default rendering at
 * all).
 *
 * THE LIGHT TRUST PATH IS UNCHANGED. Same two calls, same bodies, same
 * order, as the shipped component: `POST /coach-links/:id/accept`,
 * `DELETE /coach-links/:id`. Only the rendering and the failure path
 * differ:
 *
 *   - Accept is gold (`kit/ActionButton`, D64/D186.7 — the closed page's
 *     ONE gold element when an invite is waiting; journey 29 counts it).
 *     Decline is a bare navy text link (`kit/SecondaryAction
 *     variant="link"`) — the D33 anatomy PR #936 gave
 *     `CoachPortalEmptyState.jsx`'s own Accept/Decline pair.
 *   - A refusal is reported to the caller via `onError` and rendered ON
 *     THE PAGE by `CoachAccessCardNew.jsx` (D57 — a failure stays on the
 *     page, never a toast); a SUCCESS still toasts, because the screen
 *     does not otherwise confirm it (D109).
 *   - While either verb is in flight, BOTH buttons wait — `disabled={busy}`
 *     on each, the same "accepting" state the issue's own state list names.
 */
export default function CoachAccessInviteActionsNew({ link, onDone, onError }) {
  const [busy, setBusy] = useState(false);

  const accept = async () => {
    setBusy(true);
    onError?.(null);
    try {
      await api.post(`/coach-links/${link.id}/accept`);
      track("coach.invite_accepted");
      toast.success("Coach connected — they can now see your cricket data");
      onDone?.();
    } catch {
      onError?.("Could not accept the invite");
    }
    setBusy(false);
  };

  const decline = async () => {
    setBusy(true);
    onError?.(null);
    try {
      await api.delete(`/coach-links/${link.id}`);
      toast.success("Invite declined — nothing was shared");
      onDone?.();
    } catch {
      onError?.("Could not decline the invite");
    }
    setBusy(false);
  };

  return (
    <div
      style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 14 }}
      data-testid={`settings-coach-invite-actions-${link.id}`}
    >
      <span style={{ flexGrow: 1, display: "flex" }}>
        <ActionButton
          icon={<Check size={18} />}
          onClick={accept}
          disabled={busy}
          data-testid={`settings-coach-invite-accept-${link.id}`}
          style={{ flexGrow: 1, width: "auto", minHeight: 44 }}
        >
          {busy ? "Accepting…" : "Accept"}
        </ActionButton>
      </span>
      <SecondaryAction
        variant="link"
        onClick={decline}
        disabled={busy}
        data-testid={`settings-coach-invite-decline-${link.id}`}
        style={{ alignSelf: "center", opacity: busy ? 0.6 : 1 }}
      >
        Decline
      </SecondaryAction>
    </div>
  );
}
